import type { BackgroundValue } from '@elfsight/configurator-contracts';
import { uuid } from '@elfsight/lib-utils';
import {
  consentSettings,
  emailSettings,
  headingSettings,
  integrationsSettings,
  longTextSettings,
  phoneSettings,
  respondentNotificationSettings,
  selfNotificationSettings,
  shortTextSettings,
  submitActionsSettings
} from '@elfsight/widget-form-builder/widget';
import { timerDefaultSettings as libTimerDefaultSettings } from '@elfsight/widget-ui';
import type {
  ActionAfterCountdown,
  ActionDuringCountdownAddToCalendar,
  ActionDuringCountdownOpenForm,
  ActionDuringCountdownOpenLink,
  CountdownTimerSettings,
  CountdownTimerTheme,
  TimerSettings
} from '../types';
import { colors } from './colors';

const {
  id: _id,
  widgetId: _widgetId,
  editMode: _editMode,
  ...timerDefaultSettings
} = libTimerDefaultSettings;

const defaultOpenLinkSettings: ActionDuringCountdownOpenLink = {
  id: uuid(),
  type: 'open-link',
  buttonText: 'Shop Now',
  buttonLink: {
    type: 'url',
    value: '',
    rawValue: '',
    target: '_blank'
  },
  buttonStyle: 'primary'
};

const consentId = uuid();
export const defaultOpenFormSettings: ActionDuringCountdownOpenForm = {
  id: uuid(),
  type: 'open-form',
  buttonText: 'Contact Us',
  buttonStyle: 'primary',
  fields: [
    {
      ...headingSettings,
      id: uuid(),
      type: 'heading',
      heading: 'Contact Us',
      caption:
        'We’d love to hear from you! Fill out the form and our team will get back to you shortly.'
    },
    {
      ...shortTextSettings,
      id: uuid(),
      type: 'shortText',
      label: 'Your Name',
      fieldID: 'your_name'
    },
    {
      ...emailSettings,
      id: uuid(),
      type: 'email',
      fieldID: 'email'
    },
    {
      ...phoneSettings,
      id: uuid(),
      type: 'phone',
      label: 'Phone Number',
      fieldID: 'phone_number'
    },
    {
      ...longTextSettings,
      id: uuid(),
      type: 'longText',
      label: 'Your Message',
      fieldID: 'your_message'
    },
    {
      ...consentSettings,
      id: consentId,
      type: 'consent',
      fieldID: consentId
    }
  ],
  submitButtonText: 'Submit',
  submitRedirectURL: '',
  recaptcha: false,
  integrationSettingsID: '',
  ...submitActionsSettings,
  ...selfNotificationSettings,
  ...respondentNotificationSettings,
  ...integrationsSettings
};

const defaultAddToCalendarSettings: ActionDuringCountdownAddToCalendar = {
  id: uuid(),
  type: 'add-to-calendar',
  eventTitle: 'Event',
  eventDescription: '',
  buttonText: 'Add to Google Calendar',
  buttonStyle: 'primary'
};

export const defaultActionsDuringCountdown = {
  'open-link': defaultOpenLinkSettings,
  'open-form': defaultOpenFormSettings,
  'add-to-calendar': defaultAddToCalendarSettings
};

export const defaultActionAfterCountdown: ActionAfterCountdown = {
  type: 'show-message',
  messageText: 'The countdown is finished',
  redirectUrl: '',
  showButton: false,
  buttonLink: {
    type: 'url',
    value: '',
    rawValue: '',
    target: '_blank'
  },
  buttonText: 'Shop Now',
  buttonStyle: 'primary',
  showTimer: true
} as const;

export const defaultTimerSettings: TimerSettings = {
  ...timerDefaultSettings,
  hideBeforeCountdown: false,
  remainingRestartValue: 24,
  remainingRestartUnit: 'hours',
  timerTimeFormat: 'auto',
  timerSeparatorStyle: 'colon',
  timerStyle: 'clean',
  timerAnimation: 'none',
  timerShowLabels: true,
  timerStartNumber: 1,
  timerEndNumber: 100,
  timerCountdownMode: 'auto',
  timerCountdownFrequency: 5
} as const;

const { white, black, blue } = colors;

export const commonThemeSettings = {
  formBackgroundColor: white,
  formTextColor: black,

  cornerRadius: 'circular',

  font: {},
  headingFontSizeInline: 20,
  headingFontSizeInlineMobile: 15,
  headingFontSizeBar: 15,
  headingFontSizeBarMobile: 13,

  timerSizeInline: 32,
  timerSizeInlineMobile: 25,
  timerSizeBar: 17.6,
  timerSizeBarMobile: 16,

  buttonSizeInline: 15,
  buttonSizeInlineMobile: 14,
  buttonSizeBar: 14,
  buttonSizeBarMobile: 12
} as const;

export const defaultBackgroundSettings: BackgroundValue = {
  type: 'color' as const,
  color: white
};

const defaultThemeSettings: CountdownTimerTheme = {
  type: 'light',
  accentColor: blue,
  themeSettings: {
    ...commonThemeSettings,
    background: defaultBackgroundSettings,
    headingColor: black,
    timerColor: blue,
    timerLabelsColor: blue,
    buttonPrimaryColor: blue,
    buttonPrimaryTextColor: white,
    buttonSecondaryColor: blue,
    formBackgroundColor: white,
    formTextColor: black,
    formAccentColor: blue
  },
  colorName: 'cyan'
} as const;

export const defaultSettings: CountdownTimerSettings = {
  ...defaultTimerSettings,

  headingText: '⚡ Limited-time offer! Sale ends in',

  actionsDuringCountdown: [],
  actionAfterCountdown: defaultActionAfterCountdown,

  layout: 'inline',
  showCloseButton: true,
  widgetWidth: 640,
  contentWidth: 800,
  widgetAlignment: 'center',
  contentAlignment: 'center',

  theme: defaultThemeSettings,

  language: {
    language: 'en',
    label: 'English (United States)',
    messages: {}
  },
  googleAnalytics: [],
  customCSS: '',
  customJS: ''
};
